import { collectionQuery, patchResource, type ListParams, type PageResponse } from './collections';
import { apiFetch } from './http';
import type { EntityReference } from './requests';

/**
 * Hours (07 §4.9, 09 §10, 10 §4). Two orthogonal machines live in every row: the team leader
 * approves the work, the billing manager bills it. The console never moves either with a patch -
 * each one is a command (`SM-02`).
 */

export const APPROVAL_STATUSES = ['pending', 'approved', 'rejected'] as const;

export type ApprovalStatus = (typeof APPROVAL_STATUSES)[number];

export const BILLING_STATUSES = ['not_billable', 'pending_billing', 'in_period', 'billed'] as const;

export type BillingStatus = (typeof BILLING_STATUSES)[number];

export interface TimeEntrySummary {
  id: string;
  ticketId: string;
  ticketNumber: string;
  /** The ticket's title and its organization: what was done and for whom, even without a task (`AM-340`). */
  ticketTitle: string;
  organization: EntityReference;
  /** `AM-465` (D13): every hour belongs to a task, so the API always names it. */
  taskId: string;
  /** The task the hour was recorded on, with its title: what "Mis horas" and the approval show. */
  task: EntityReference;
  agent: EntityReference;
  workType: EntityReference;
  workDate: string;
  durationMinutes: number;
  isChargeable: boolean;
  approvalStatus: ApprovalStatus;
  billingStatus: BillingStatus;
  rateResolutionStatus: string;
  rateResolutionReason?: string | null;
  billableAmount?: number | null;
  currency?: string | null;
  isLocked: boolean;
  createdAt: string;
  updatedAt: string;
  /**
   * Whether the hour is meant to be charged ("Facturable"), the only thing that decides it
   * (`AM-382`); `isChargeable` (`AM-554`) answers it only once a rate resolved (`AM-182`). The "Tiempo" tab of
   * every ticket shows and changes it on the row (`AM-370`, `AM-382`).
   */
  billableIntent: boolean;
  /**
   * The column "Facturación" of the hour (D3), worked out once by the server: "not_billable",
   * "contract_non_billable", "no_price", "awaiting_approval", "pending_billing", "in_draft" or "billed".
   */
  billing: string;
  /** `AM-386`: why an hour whose box is unticked is not billed, one of the tenant's reasons; optional. */
  nonBillableReason?: EntityReference | null;
  /**
   * Whoever wrote the hour, an author of it as much as its agent: who may not keep it approved by
   * editing it (`AM-294`), which the "Tiempo" tab asks before ticking "Facturable" (`AM-382`).
   */
  recordedBy?: EntityReference | null;
  eTag: string;
  /**
   * `AM-420` (D9): the billable minutes, the duration after the minimum and the rounding of the rule
   * that measures the hour: its estimate before the close, what its charge says after it.
   */
  billableMinutes: number;
  /** The billing rule that measured them; none where no rule answered. */
  billingRule?: EntityReference | null;
  /** `AM-463`: who approved it ("Aprobada por"); none for a pending or rejected hour, or one approved with no review. */
  approvedBy?: EntityReference | null;
  /** `AM-203`, `AM-463`: `automatic` when the hour was born approved because the tenant does not require approval. */
  approvalSource: 'manual' | 'automatic';
  /** `AM-463`: why it was rejected ("Motivo del rechazo"). */
  rejectionReason?: string | null;
  /** `AM-464`: the contract that governs the hour, "number - name"; none without one. */
  contract?: EntityReference | null;
  /**
   * `AM-464`: the origin of its price: "rate_card", "customer_rate", "general_rate" or "covered_by_contract";
   * none without a price.
   */
  priceOrigin?: string | null;
  /** `AM-464`: the rate card whose version fixed the price, and the day that version starts. */
  rateCard?: EntityReference | null;
  rateCardVersionFrom?: string | null;
  /** `AM-464`: the document the hour is on (no number while a draft), for its column "Comprobante". */
  invoice?: { id: string; documentNumber?: string | null; status: string } | null;
  /**
   * `AM-666`, `AM-630`: the column "Precio por hora" (the price per hour before the multiplier of its rule), only for whoever holds `rate_cards.view` (`AM-530`), as
   * `billableAmount` ("Importe estimado"); for an hour past the bag of an Abono con paquete de horas, the
   * precio por hora del excedente (`AM-542`).
   */
  billingRate?: number | null;
  /** `AM-634`: the mark "De un período anterior" the close set on the hour. */
  fromEarlierPeriod?: boolean;
  /** `AM-634`: its adjustments, which the "Horas" tab lists under it. */
  adjustments?: TimeEntryAdjustmentLine[];
  /**
   * `AM-811` (decision 49 of the 52): the contract covers the hour with no money of its own, "Cubierta por el
   * contrato" in "Importe estimado", also inside the bag of a Paquete de horas sin abono.
   */
  coveredByContract?: boolean;
}

/** `AM-634`: an adjustment as the list of hours carries it: signed minutes, reason and its "Facturación". */
export interface TimeEntryAdjustmentLine {
  id: string;
  adjustmentMinutes: number;
  reason: string;
  billing?: string | null;
  createdAt: string;
}

export interface TimeEntryAdjustmentEntry {
  id: string;
  adjustmentMinutes: number;
  billableAmount?: number | null;
  costAmount?: number | null;
  currency?: string | null;
  reason: string;
  approvedBy?: EntityReference | null;
  createdAt: string;
  /**
   * `AM-399` (D5 point 1): its "Facturación" - "pending_billing", "in_draft" or "billed" if it adds
   * minutes, "to_credit" or "credited" if it takes them away - or null when it moves no money.
   */
  billing?: string | null;
  /** The credit note that gives back the minutes a negative adjustment took away; no number while a draft. */
  creditNote?: EntityReference | null;
}

/**
 * `AM-399`: what an adjustment would move, read before saving. `amount` is signed and before tax, at
 * the price the hour was billed at, or null when it moves no money; for minutes taken away, `invoice`
 * is the document the credit note goes on and `creditable` whether it still admits one.
 */
export interface AdjustmentQuote {
  adjustmentMinutes: number;
  amount?: number | null;
  currency?: string | null;
  invoice?: EntityReference | null;
  creditable: boolean;
}

/** The detail does not carry the ticket's title nor its organization: only the summary does (`AM-340`). */
export interface TimeEntryDetail extends Omit<
  TimeEntrySummary,
  'ticketTitle' | 'organization' | 'adjustments'
> {
  startedAt?: string | null;
  endedAt?: string | null;
  description?: string | null;
  costRate?: number | null;
  costAmount?: number | null;
  approvedAt?: string | null;
  /**
   * `AM-465`: "Cómo se cargó": "manual", "timer", "quick_ticket" or "ticket_cancellation"; none on an hour written
   * before it was kept.
   */
  entrySource?: string | null;
  isRetroactive: boolean;
  adjustments: TimeEntryAdjustmentEntry[];
}

/** What a write answers; `rate_unresolved` is a warning and not an error (`FIN-11`). */
export interface TimeEntryWritten {
  entry: TimeEntryDetail;
  warnings: string[];
}

export interface WorkTimerDetail {
  id: string;
  ticketId: string;
  ticketNumber: string;
  taskId?: string | null;
  startedAt: string;
  stoppedAt?: string | null;
  timeEntryId?: string | null;
  /** `AM-462`: rounded as the hour the clock leaves, to the nearest minute and at least one. */
  elapsedMinutes: number;
  /**
   * `AM-462`: how the clock stands: "running", "stopped", "discarded", "stopped_on_cancellation" or
   * "discarded_on_cancellation". No screen names these states yet.
   */
  state: string;
}

export interface RecordTimeEntryInput {
  ticketId: string;
  taskId?: string | null;
  workTypeId: string;
  workDate?: string | null;
  durationMinutes: number;
  description?: string | null;
  isBillable?: boolean | null;
  agentUserId?: string | null;
}

export interface StopTimerInput {
  workTypeId: string;
  description?: string | null;
  isBillable?: boolean | null;
  taskId?: string | null;
}

/**
 * A merge patch (RFC 7396); it never carries a state of either machine (`SM-02`). `isBillable` is the
 * intent to charge the work, as when recording it, not a move of the billing status (`AM-294`).
 */
export type UpdateTimeEntryInput = Record<string, unknown> & {
  taskId?: string | null;
  description?: string | null;
  durationMinutes?: number;
  workDate?: string;
  workTypeId?: string;
  isBillable?: boolean;
  /** `AM-386`: the reason of an unticked hour, or `null` for none. */
  nonBillableReasonId?: string | null;
};

/**
 * A page of `GET /time-entries`: the page of every collection plus `totalMinutes`, the minutes of
 * every hour the filters leave and not only of the page, added up by the server so the total under
 * "Mis horas" does not change with "Cargar más", and `rejectedMinutes`, the part of it that belongs
 * to rejected hours, which the "Tiempo" tab of a ticket (`AM-351`) and "Mis horas" (`AM-379`) leave
 * out of their totals. The other screens that read the list ignore both.
 */
export interface TimeEntryPage extends PageResponse<TimeEntrySummary> {
  totalMinutes: number;
  rejectedMinutes: number;
  /** D4: the minutes that count, rejected ones left out, by the value of the column "Facturación". */
  minutesByBilling?: Record<string, number>;
}

export interface BulkApproveOutcome {
  id: string;
  approved: boolean;
  errorCode?: string | null;
}

export const timeEntriesApi = {
  list: (params: ListParams, signal?: AbortSignal): Promise<TimeEntryPage> =>
    apiFetch<TimeEntryPage>(
      `/api/v1/time-entries${collectionQuery(params)}`,
      signal === undefined ? {} : { signal },
    ),
  get: (id: string, signal?: AbortSignal): Promise<TimeEntryDetail> =>
    apiFetch<TimeEntryDetail>(`/api/v1/time-entries/${id}`, signal === undefined ? {} : { signal }),
  /**
   * `idempotencyKey` (`AM-18`): the same key with the same body replays the first answer instead of
   * recording the hours twice, which is what a retry over a flaky connection needs.
   */
  record: (body: RecordTimeEntryInput, idempotencyKey?: string): Promise<TimeEntryWritten> =>
    apiFetch<TimeEntryWritten>('/api/v1/time-entries', {
      method: 'POST',
      body,
      ...(idempotencyKey === undefined ? {} : { headers: { 'Idempotency-Key': idempotencyKey } }),
    }),
  update: (id: string, body: UpdateTimeEntryInput, etag: string): Promise<TimeEntryWritten> =>
    patchResource<TimeEntryWritten>(`/api/v1/time-entries/${id}`, body, etag),
  /**
   * Withdraws an hour recorded by mistake, under the guards of the edit: the row stays on the
   * server and leaves every reading (migration 0050). The ETag makes a stale screen fail instead of
   * withdrawing an hour somebody changed meanwhile.
   */
  remove: (id: string, etag: string): Promise<void> =>
    apiFetch<void>(`/api/v1/time-entries/${id}`, {
      method: 'DELETE',
      headers: { 'If-Match': etag },
    }),
  approve: (id: string): Promise<TimeEntryWritten> =>
    apiFetch<TimeEntryWritten>(`/api/v1/time-entries/${id}/approve`, { method: 'POST' }),
  /**
   * `AM-465`: the work type the dialogs that create an hour preselect ("Tipo de trabajo propuesto"): the one the type
   * of the task proposes or the service offering of the ticket; `null` when neither does.
   */
  proposedWorkType: async (
    ticketId: string,
    taskId: string | null,
    signal?: AbortSignal,
  ): Promise<string | null> =>
    (
      await apiFetch<{ workTypeId?: string | null }>(
        `/api/v1/time-entries/proposed-work-type?ticketId=${encodeURIComponent(ticketId)}${
          taskId === null ? '' : `&taskId=${encodeURIComponent(taskId)}`
        }`,
        signal === undefined ? {} : { signal },
      )
    ).workTypeId ?? null,
  /** `AM-463`: "Volver a Pendiente" of an hour approved by mistake, while it is in no document. */
  unapprove: (id: string): Promise<TimeEntryWritten> =>
    apiFetch<TimeEntryWritten>(`/api/v1/time-entries/${id}/unapprove`, { method: 'POST' }),
  reject: (id: string, reason: string): Promise<TimeEntryWritten> =>
    apiFetch<TimeEntryWritten>(`/api/v1/time-entries/${id}/reject`, {
      method: 'POST',
      body: { reason },
    }),
  /** Each id is its own transaction: one that fails its guard does not block the rest (`DA-10`). */
  bulkApprove: async (ids: readonly string[]): Promise<BulkApproveOutcome[]> =>
    (
      await apiFetch<{ items: BulkApproveOutcome[] }>('/api/v1/time-entries/bulk-approve', {
        method: 'POST',
        body: { ids },
      })
    ).items,
  /**
   * The only correction of an hour already billed or otherwise locked (`FIN-02`, `FIN-32`, P002):
   * minutes, in more or in less, and a reason. `idempotencyKey` for the same reason a record or a
   * stop takes one - a retry over a flaky connection replays the answer instead of doubling it.
   */
  /**
   * `AM-464`: "Recalcular el precio": prices the hour again with the contract, the rate card and the rule that rule
   * today. Never an hour in a document (the server refuses it).
   */
  recalculatePrice: (id: string): Promise<unknown> =>
    apiFetch<unknown>(`/api/v1/time-entries/${id}/resolve-rate`, { method: 'POST' }),
  /** `AM-399`: what adjusting the hour by `minutes` (signed) would move, for the dialog to say. */
  adjustmentQuote: (id: string, minutes: number): Promise<AdjustmentQuote> =>
    apiFetch<AdjustmentQuote>(
      `/api/v1/time-entries/${id}/adjustments/quote?minutes=${encodeURIComponent(minutes)}`,
    ),
  adjust: (
    id: string,
    body: { adjustmentMinutes: number; reason: string },
    idempotencyKey?: string,
  ): Promise<TimeEntryWritten> =>
    apiFetch<TimeEntryWritten>(`/api/v1/time-entries/${id}/adjustments`, {
      method: 'POST',
      body,
      ...(idempotencyKey === undefined ? {} : { headers: { 'Idempotency-Key': idempotencyKey } }),
    }),
};

/** `AM-546`: a clock the cancellation of its ticket closed, with its agent and how it stands. */
export interface CancelledTicketTimer {
  id: string;
  agent: { id: string; displayName: string };
  state: 'stopped_on_cancellation' | 'discarded_on_cancellation';
}

export const timersApi = {
  /** `AM-546` (C04-08): the clocks the cancellation of the ticket closed that the reader reaches. */
  cancelled: async (ticketId: string, signal?: AbortSignal): Promise<CancelledTicketTimer[]> =>
    (
      await apiFetch<{ items: CancelledTicketTimer[] }>(
        `/api/v1/tickets/${ticketId}/cancelled-timers`,
        signal === undefined ? {} : { signal },
      )
    ).items,
  /**
   * The answer carries `null` when there is no clock running (07 §1.15); a mock that omits the
   * property has to become the same explicit null: a query that resolves to undefined is an error
   * for the cache, not an answer.
   */
  current: async (signal?: AbortSignal): Promise<WorkTimerDetail | null> =>
    (
      await apiFetch<{ timer?: WorkTimerDetail | null }>(
        '/api/v1/timers/current',
        signal === undefined ? {} : { signal },
      )
    ).timer ?? null,
  start: (ticketId: string, taskId?: string | null): Promise<WorkTimerDetail> =>
    apiFetch<WorkTimerDetail>('/api/v1/timers/start', {
      method: 'POST',
      body: { ticketId, taskId: taskId ?? null },
    }),
  stop: (id: string, body: StopTimerInput, idempotencyKey?: string): Promise<TimeEntryWritten> =>
    apiFetch<TimeEntryWritten>(`/api/v1/timers/${id}/stop`, {
      method: 'POST',
      body,
      ...(idempotencyKey === undefined ? {} : { headers: { 'Idempotency-Key': idempotencyKey } }),
    }),
  discard: (id: string): Promise<WorkTimerDetail> =>
    apiFetch<WorkTimerDetail>(`/api/v1/timers/${id}/discard`, { method: 'POST' }),
};

/**
 * Minutes as the console shows them: "1 h 30 min", "1 h 00 min", "0 h 20 min", never a decimal of hours
 * nor a clock face (`AM-438`, rule 7 of the billing model). A negative amount (a correction) keeps its
 * sign in front: "-1 h 30 min".
 */
export function formatMinutes(minutes: number): string {
  const sign = minutes < 0 ? '-' : '';
  const total = Math.round(Math.abs(minutes));
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  return `${sign}${hours} h ${String(rest).padStart(2, '0')} min`;
}

/** `AM-386`: a reason of the tenant for not billing an hour. */
export interface NonBillableReason {
  id: string;
  name: string;
  isActive: boolean;
}

export const nonBillableReasonsApi = {
  list: (signal?: AbortSignal): Promise<NonBillableReason[]> =>
    apiFetch<{ items: NonBillableReason[] }>(
      '/api/v1/non-billable-reasons',
      signal === undefined ? {} : { signal },
    ).then((answer) => answer.items),
  create: (name: string): Promise<NonBillableReason> =>
    apiFetch<NonBillableReason>('/api/v1/non-billable-reasons', { method: 'POST', body: { name } }),
  update: (id: string, name: string, isActive: boolean): Promise<NonBillableReason> =>
    apiFetch<NonBillableReason>(`/api/v1/non-billable-reasons/${id}`, {
      method: 'PUT',
      body: { name, isActive },
    }),
};
